import React, { FC, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useBrandGroupConfig } from '../../hooks/useBrandGroupConfig';
import { useAuthenticationContext } from '../../providers/AuthenticationProvider';
import { RegistrationFlowField } from '../../types/apiConfig';
import { GENDER_CODE_CIAM, RegistrationFlowProps } from '../../types/registrationFlows';
import { mapGenderCodesToFormikRadioButtonOptions } from '../../utils/mapGenderCodesToFormikRadioButtonOptions';
import { RegistrationSchemaBuilder } from '../../utils/registrationSchemaBuilder';
import { FormikAddressForm } from '../WrapperComponents/FormikAddressForm';
import { FormikDatePicker } from '../WrapperComponents/FormikDatePicker';
import { FormikErrorTooltip } from '../WrapperComponents/FormikErrorTooltip';
import { FormikOptinList } from '../WrapperComponents/FormikOptinList';
import { FormikRadioButtonPicker } from '../WrapperComponents/FormikRadioButtonPicker';
import { FormikTextField } from '../WrapperComponents/FormikTextField';
import './registrationFlowForm.css';
import { RegistrationFlowHeader } from './RegistrationFlowHeader';
import { Form, Formik } from 'formik';
import { Button, Caption, Fieldset, Stack } from '@mediahuis/chameleon-react';

export const RegistrationFlowForm: FC<RegistrationFlowProps> = ({
  initialFormValues,
  onSubmit,
  validateOnChange,
  validationProperties,
  addressServiceLanguage,
  countryConfig,
  orchestration,
  onCountryChange,
  onSubmitClick,
}) => {
  const { t } = useTranslation();
  const { user } = useAuthenticationContext();
  const { brandConfig } = useBrandGroupConfig();
  const validationSchema = useMemo(
    () =>
      new RegistrationSchemaBuilder(
        validationProperties,
        brandConfig?.countryISO,
        addressServiceLanguage,
        t
      )
        .withConfig(orchestration, countryConfig)
        .build(),
    [
      validationProperties,
      brandConfig?.countryISO,
      orchestration,
      addressServiceLanguage,
      countryConfig,
      t,
    ]
  );

  return (
    <>
      <RegistrationFlowHeader
        title={t('register.disclaimers.title', { email: user?.email })}
        subtitle={t('register.disclaimers.subtext')}
      />
      <Formik
        initialValues={initialFormValues}
        onSubmit={(values) => onSubmit(values)}
        validateOnBlur={false}
        validateOnChange={validateOnChange}
        enableReinitialize={false}
        validationSchema={validationSchema}>
        {(formik) => {
          const { values, submitForm, isValidating } = formik;
          const otherGenderSelected = values.gender === GENDER_CODE_CIAM.OTHER;
          const hasAnyAddressElementsVisible = [
            RegistrationFlowField.CITY,
            RegistrationFlowField.HOUSE_NUMBER,
            RegistrationFlowField.STREET,
            RegistrationFlowField.BOX_NUMBER,
            RegistrationFlowField.POST_CODE,
            RegistrationFlowField.COUNTRY,
          ].some((f) => orchestration.visibleFields.includes(f));

          return (
            <Form noValidate={true}>
              <Stack>
                {(orchestration.visibleFields.includes(RegistrationFlowField.FIRST_NAME) ||
                  orchestration.visibleFields.includes(RegistrationFlowField.LAST_NAME)) && (
                  <Stack
                    className="naming__inputs mb-3 w-100 g-7"
                    flexDirection={{
                      sm: 'row',
                      md: 'row',
                      lg: 'row',
                      xl: 'row',
                    }}
                    flexWrap={'nowrap'}>
                    <FormikTextField
                      name="firstName"
                      id="firstName"
                      label={t('register.naming.firstName')}
                      formikProps={formik}
                      data-cy="firstName"
                    />
                    <FormikTextField
                      name="lastName"
                      id="lastName"
                      label={t('register.naming.lastName')}
                      formikProps={formik}
                      data-cy="lastName"
                    />
                  </Stack>
                )}
                {orchestration.visibleFields.includes(RegistrationFlowField.BIRTH_DATE) && (
                  <Stack flexDirection={'column'} className="mb-3 w-100 g-3">
                    <FormikDatePicker label={t('register.birthDate.date')} formikProps={formik} />
                  </Stack>
                )}
                {orchestration.visibleFields.includes(RegistrationFlowField.GENDER) && (
                  <Stack className="mb-3">
                    <Fieldset
                      label={t('register.gender.label')}
                      className="gender-option__fieldset label">
                      <Stack flexDirection="column">
                        <Stack
                          className="gender-option__radio"
                          flexDirection={{
                            sm: 'row',
                            md: 'row',
                            lg: 'row',
                            xl: 'row',
                          }}
                          flexWrap={'nowrap'}>
                          <FormikRadioButtonPicker
                            name="gender"
                            formikProps={formik}
                            options={mapGenderCodesToFormikRadioButtonOptions(values)}
                          />
                        </Stack>
                        {otherGenderSelected && (
                          <Stack>
                            <FormikTextField
                              name="genderOther"
                              id="genderOther"
                              labelHidden
                              className="w-100"
                              placeholder={t([
                                'register.gender.otherPlaceholder',
                                'register.gender.label',
                              ])}
                              formikProps={formik}
                              data-cy="genderCustomFreeTextInput"
                            />
                          </Stack>
                        )}
                        <FormikErrorTooltip fieldName="gender" />
                      </Stack>
                    </Fieldset>
                  </Stack>
                )}
                {hasAnyAddressElementsVisible && (
                  <FormikAddressForm
                    formikProps={formik}
                    countryConfig={countryConfig}
                    onCountryChange={onCountryChange}
                    orchestration={orchestration}
                  />
                )}
                {orchestration.visibleFields.includes(RegistrationFlowField.PHONE_NUMBER) && (
                  <>
                    <FormikTextField
                      name="telephone"
                      id="telephone"
                      label={t('register.telephone')}
                      formikProps={formik}
                      placeholder={countryConfig.phoneNumberFormat}
                      data-cy="telephoneInput"
                      className="mb-1 mt-0"
                    />
                    <div className="mb-3 mt-0">
                      {!formik.errors.telephone && (
                        <Caption
                          size={'lg'}
                          className={formik.errors.telephone ? 'red-base mb-3' : 'grey-50 mb-3'}>
                          <Trans
                            i18nKey={'register.telephoneHelp'}
                            values={{ phoneNumberFormat: countryConfig.phoneNumberFormat }}
                          />
                        </Caption>
                      )}
                    </div>
                  </>
                )}
                <Stack justifyContent="center" className="mb-3">
                  <FormikOptinList formikProps={formik} />
                </Stack>
                <Stack>
                  <Button
                    onClick={() => {
                      onSubmitClick();
                      return submitForm();
                    }}
                    type="button"
                    disabled={isValidating}
                    width="full"
                    appearance="primary"
                    data-cy="confirmButton">
                    {t('register.confirmSignUp')}
                  </Button>
                </Stack>
              </Stack>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};
